import React, { useContext, useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '@/App';
import { Zap, Target, Brain, Users, ArrowRight, Flame, Sparkles, Globe, Check, ChevronDown, Star, Shield, Rocket, Camera, UtensilsCrossed, Dumbbell } from 'lucide-react';

// New Landing Components - WOW Effects
import KetoneMolecules from '@/components/landing/KetoneMolecules';
import EcosystemMap from '@/components/landing/EcosystemMap';
import LiveStatsCounter from '@/components/landing/LiveStatsCounter';
import DayWithKetoLab from '@/components/landing/DayWithKetoLab';
import GamificationPreview from '@/components/landing/GamificationPreview';
import TestimonialsSection from '@/components/landing/TestimonialsSection';
import SpiritScanPublic from '@/components/SpiritScanPublic';

// ============================================================================
// ENERGIA VERDE NÉON - PARTÍCULAS ANIMADAS (Memoized para evitar re-render)
// ============================================================================
const EnergyParticles = React.memo(() => {
  const canvasRef = useRef(null);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    let animationId;
    let particles = [];
    
    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    
    resize();
    window.addEventListener('resize', resize);
    
    // Criar partículas
    const createParticle = () => ({
      x: Math.random() * canvas.width,
      y: canvas.height + 10,
      size: Math.random() * 3 + 1,
      speedY: Math.random() * 2 + 1,
      speedX: (Math.random() - 0.5) * 0.5,
      opacity: Math.random() * 0.5 + 0.3,
      hue: Math.random() * 20 + 75, // Verde néon variado
    });
    
    // Inicializar partículas
    for (let i = 0; i < 50; i++) {
      const p = createParticle();
      p.y = Math.random() * canvas.height;
      particles.push(p);
    }
    
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      particles.forEach((p, i) => {
        p.y -= p.speedY;
        p.x += p.speedX;
        p.opacity -= 0.002;
        
        if (p.y < -10 || p.opacity <= 0) {
          particles[i] = createParticle();
        }
        
        // Desenhar partícula com glow
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `hsla(${p.hue}, 100%, 60%, ${p.opacity})`;
        ctx.shadowBlur = 15;
        ctx.shadowColor = `hsla(${p.hue}, 100%, 60%, ${p.opacity})`;
        ctx.fill();
      });
      
      animationId = requestAnimationFrame(animate);
    };
    
    animate();
    
    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationId);
    };
  }, []);
  
  return (
    <canvas 
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none z-0"
      style={{ opacity: 0.6 }}
    />
  );
});

// ============================================================================
// EXPLOSÃO DE ENERGIA CENTRAL (Memoized para evitar re-render)
// ============================================================================
const EnergyBurst = React.memo(() => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {/* Central Energy Burst */}
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
      {/* Core glow */}
      <div className="w-[600px] h-[600px] rounded-full bg-[#a3e635] opacity-[0.03] blur-[100px] animate-pulse-slow" />
      
      {/* Rings */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-[400px] h-[400px] rounded-full border border-[#a3e635]/10 animate-ping-slow" />
      </div>
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-[500px] h-[500px] rounded-full border border-[#a3e635]/5 animate-ping-slower" />
      </div>
    </div>
    
    {/* Floating Orbs */}
    <div className="absolute top-[10%] left-[10%] w-32 h-32 rounded-full bg-[#a3e635]/10 blur-3xl animate-float" />
    <div className="absolute top-[20%] right-[15%] w-40 h-40 rounded-full bg-[#39FF14]/10 blur-3xl animate-float-delayed" />
    <div className="absolute bottom-[20%] left-[20%] w-36 h-36 rounded-full bg-[#a3e635]/10 blur-3xl animate-float-slow" />
    <div className="absolute bottom-[10%] right-[10%] w-28 h-28 rounded-full bg-[#39FF14]/10 blur-3xl animate-float" />
    
    {/* Energy Lines */}
    <div className="absolute top-0 left-1/2 w-px h-full bg-gradient-to-b from-transparent via-[#a3e635]/20 to-transparent" />
    <div className="absolute top-1/2 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#a3e635]/20 to-transparent" />
  </div>
));

// ============================================================================
// FEATURE CARD COM EFEITO NÉON
// ============================================================================
const NeonFeatureCard = ({ icon: Icon, title, description, delay = 0 }) => (
  <div 
    className="group relative p-5 sm:p-6 rounded-xl bg-gray-900/80 backdrop-blur-xl border border-gray-800
               hover:border-[#a3e635]/40 transition-all duration-300 hover:scale-[1.02]
               shadow-lg shadow-black/40 hover:shadow-[0_0_25px_rgba(163,230,53,0.15)] overflow-hidden"
    style={{ animationDelay: `${delay}ms` }}
  >
    {/* Hover glow effect */}
    <div className="absolute inset-0 bg-gradient-to-br from-[#a3e635]/0 via-[#a3e635]/5 to-[#a3e635]/0 
                    opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
    
    {/* Icon */}
    <div className="relative w-12 h-12 sm:w-14 sm:h-14 mb-4 rounded-lg bg-[#a3e635]/10
                    flex items-center justify-center group-hover:bg-[#a3e635]/20 transition-all duration-300
                    border border-[#a3e635]/20 group-hover:border-[#a3e635]/40">
      <Icon className="w-6 h-6 sm:w-7 sm:h-7 text-[#a3e635]" />
    </div>
    
    <h3 className="text-lg sm:text-xl font-bold text-white mb-2 group-hover:text-[#a3e635] transition-colors">
      {title}
    </h3>
    <p className="text-gray-400 text-sm leading-relaxed">
      {description}
    </p>
  </div>
);

// ============================================================================
// STAT CARD ANIMADO
// ============================================================================
const AnimatedStatCard = ({ value, label, icon: Icon }) => (
  <div className="relative group">
    <div className="relative p-4 sm:p-5 rounded-xl bg-gray-900/80 backdrop-blur-sm border border-gray-800
                    group-hover:border-[#a3e635]/40 transition-all duration-300 text-center min-h-[100px] sm:min-h-[110px] 
                    flex flex-col justify-center shadow-lg shadow-black/30">
      {Icon && <Icon className="w-5 h-5 text-[#a3e635] mx-auto mb-2" />}
      <div className="text-2xl sm:text-3xl font-black text-[#a3e635] leading-tight">
        {value}
      </div>
      <div className="text-[10px] sm:text-xs text-gray-400 mt-1.5 uppercase tracking-wider line-clamp-2">{label}</div>
    </div>
  </div>
);

// ============================================================================
// LANDING PAGE PRINCIPAL
// ============================================================================
const LandingPagePremium = () => {
  const { login, register } = useContext(AuthContext);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [isLogin, setIsLogin] = useState(false); // Default to REGISTER mode for new users
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showLangMenu, setShowLangMenu] = useState(false);
  const [currentLang, setCurrentLang] = useState(i18n.language || 'en');
  const [scrolled, setScrolled] = useState(false);
  const [showSpiritScan, setShowSpiritScan] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const changeLanguage = (lng) => {
    if (lng === currentLang) {
      setShowLangMenu(false);
      return;
    }
    
    // Change language first
    localStorage.setItem('language', lng);
    i18n.changeLanguage(lng);
    setCurrentLang(lng);
    setShowLangMenu(false);
    
    // Force page reload to apply translations
    window.location.reload();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (isLogin) {
        await login(email, password);
        navigate('/hub');
      } else {
        await register(email, password, name);
        // Novos registos vão para página de planos
        navigate('/plans');
      }
    } catch (err) {
      // Robust error message extraction — handles all FastAPI error shapes:
      //   - 400/401/403/500 with {"detail": "string"}
      //   - 422 with {"detail": [{"msg": "...", "loc": [...]}]}
      //   - Network errors with no response
      const detail = err?.response?.data?.detail;
      let msg = '';
      if (typeof detail === 'string') {
        msg = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        msg = detail.map(d => (d && typeof d.msg === 'string') ? d.msg : '').filter(Boolean).join(' • ');
      } else if (detail && typeof detail.msg === 'string') {
        msg = detail.msg;
      }
      if (!msg) {
        msg = err?.message && err.message !== 'Network Error'
          ? err.message
          : (isLogin ? t('auth.loginFailed') : t('auth.registerFailed'));
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="landing-page-dark min-h-screen bg-black relative overflow-x-hidden" style={{ backgroundColor: '#0a0a0a' }}>
      {/* Background Effects - Ketone Molecules with biohacking aesthetic */}
      <KetoneMolecules key="ketones" />
      <EnergyBurst key="burst" />
      
      {/* Gradient Overlay */}
      <div className="fixed inset-0 bg-gradient-to-b from-black/50 via-transparent to-black/80 pointer-events-none z-[1]" />
      
      {/* ================================================================
          NAVBAR - FUNDO PRETO SÓLIDO
      ================================================================ */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black border-b border-[#a3e635]/30">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <img 
                src="/logo-ketolab-method.png" 
                alt="Keto Lab Method" 
                className="h-12 md:h-16 w-auto object-contain"
                loading="eager"
              />
            </div>

            {/* Language Selector */}
            <button
              onClick={() => setShowLangMenu(!showLangMenu)}
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-black/50 backdrop-blur-sm
                         border border-[#a3e635]/30 hover:border-[#a3e635] transition-all duration-300
                         hover:shadow-lg hover:shadow-[#a3e635]/20"
            >
              <Globe className="w-4 h-4 text-[#a3e635]" />
              <span className="text-white text-sm font-bold">{currentLang.toUpperCase()}</span>
              <ChevronDown className={`w-4 h-4 text-[#a3e635] transition-transform ${showLangMenu ? 'rotate-180' : ''}`} />
            </button>
          </div>
        </div>
      </nav>

      {/* Language Modal - Rendered via Portal to body for proper stacking */}
      {showLangMenu && ReactDOM.createPortal(
        <div 
          className="fixed inset-0 z-[9999] flex items-center justify-center"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.85)' }}
        >
          {/* Backdrop click handler */}
          <div 
            className="absolute inset-0"
            onClick={() => setShowLangMenu(false)}
          />
          {/* Modal content */}
          <div 
            className="relative bg-zinc-900 border-2 border-[#a3e635]/50 rounded-2xl p-6 mx-4 w-full max-w-sm shadow-[0_0_30px_rgba(163,230,53,0.3)]"
            onClick={e => e.stopPropagation()}
          >
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Globe className="w-5 h-5 text-[#a3e635]" />
              {t('landing.selectLanguage')}
            </h3>
            <div className="space-y-3">
              {[
                { code: 'pt', flag: '🇵🇹', name: 'Português (PT)' },
                { code: 'pt-BR', flag: '🇧🇷', name: 'Português (BR)' },
                { code: 'es', flag: '🇪🇸', name: 'Español' },
                { code: 'en', flag: '🇬🇧', name: 'English' }
              ].map(lang => (
                <button
                  key={lang.code}
                  onClick={() => changeLanguage(lang.code)}
                  className={`w-full flex items-center justify-between p-4 rounded-xl transition-all duration-200 ${
                    i18n.language === lang.code 
                      ? 'bg-[#a3e635]/20 border-2 border-[#a3e635]' 
                      : 'bg-zinc-800/50 border-2 border-transparent hover:border-[#a3e635]/50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{lang.flag}</span>
                    <span className="text-white font-medium">{lang.name}</span>
                  </div>
                  {i18n.language === lang.code && <Check className="w-5 h-5 text-[#a3e635]" />}
                </button>
              ))}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ================================================================
          HERO SECTION - EXPLOSÃO DE ENERGIA
      ================================================================ */}
      <section className="relative z-10 min-h-screen flex items-center justify-center pt-20 pb-10 px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center">
            {/* Badge Premium - BIOHACKING */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#a3e635]/10 border border-[#a3e635]/30 mb-8 animate-fade-in-up">
              <Sparkles className="w-4 h-4 text-[#a3e635] animate-pulse" />
              <span className="text-[#a3e635] text-sm font-medium tracking-wider uppercase">
                {t('landing.biohackingBadge')}
              </span>
              <Sparkles className="w-4 h-4 text-[#a3e635] animate-pulse" />
            </div>
            
            {/* Emotional Hook */}
            <p className="text-base sm:text-lg text-gray-400 mb-4 animate-fade-in-up animation-delay-50">
              {t('landing.heroEmotion')}
            </p>
            
            {/* Main Title - KETO LAB METHOD */}
            <h1 className="text-5xl sm:text-6xl lg:text-8xl font-black mb-8 leading-[0.9] tracking-tight animate-fade-in-up animation-delay-100">
              <span className="bg-gradient-to-r from-[#a3e635] via-[#39FF14] to-[#a3e635] bg-clip-text text-transparent
                             bg-[length:200%_auto] animate-gradient-x drop-shadow-[0_0_30px_rgba(163,230,53,0.5)]">
                Keto Lab
              </span>
              <br />
              <span className="text-white"
                style={{ textShadow: '0 0 40px rgba(163, 230, 53, 0.3)' }}>
                Method
              </span>
            </h1>
            
            {/* Tagline */}
            <p className="text-2xl sm:text-3xl text-[#a3e635] font-bold mb-6 tracking-wide animate-fade-in-up animation-delay-150">
              {t('landing.taglineNew')}
            </p>
            
            {/* Subtitle */}
            <p className="text-lg sm:text-xl text-gray-300 mb-12 max-w-2xl mx-auto leading-relaxed animate-fade-in-up animation-delay-200">
              {t('landing.subtitleNew')}
            </p>
            
            {/* CTA Buttons - PREMIUM DESIGN */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center animate-fade-in-up animation-delay-300">
              {/* Primary CTA - Explosive Green */}
              <a 
                href="#auth" 
                className="group relative px-10 py-5 rounded-full overflow-hidden
                           bg-gradient-to-r from-[#a3e635] via-[#39FF14] to-[#a3e635]
                           text-black font-bold text-lg min-h-[52px]
                           shadow-[0_0_30px_rgba(163,230,53,0.5),0_0_60px_rgba(163,230,53,0.3)]
                           hover:shadow-[0_0_50px_rgba(163,230,53,0.8),0_0_100px_rgba(163,230,53,0.4)]
                           hover:scale-105 active:scale-95
                           transition-all duration-300"
              >
                {/* Animated shine effect */}
                <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent
                                -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                {/* Pulse ring on hover */}
                <span className="absolute inset-0 rounded-full border-2 border-white/30 
                               scale-100 group-hover:scale-110 group-hover:opacity-0 transition-all duration-500" />
                <span className="relative flex items-center gap-3">
                  {t('landing.startNow')}
                  <Rocket className="w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                </span>
              </a>
              
              {/* Secondary CTA - Outline with Glow */}
              <a 
                href="#features" 
                className="group relative px-8 py-4 rounded-full overflow-hidden
                           bg-transparent border-2 border-[#a3e635]
                           text-[#a3e635] font-semibold min-h-[48px]
                           shadow-[0_0_15px_rgba(163,230,53,0.2)]
                           hover:bg-[#a3e635]/10 
                           hover:shadow-[0_0_30px_rgba(163,230,53,0.4)]
                           hover:text-[#39FF14] hover:border-[#39FF14]
                           transition-all duration-300
                           flex items-center gap-2"
              >
                {/* Inner glow on hover */}
                <span className="absolute inset-0 bg-gradient-to-r from-[#a3e635]/0 via-[#a3e635]/10 to-[#a3e635]/0
                               opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <span className="relative flex items-center gap-2">
                  {t('landing.learnMore')}
                  <ChevronDown className="w-4 h-4 animate-bounce" />
                </span>
              </a>
            </div>
            
            {/* Trust Badge - Guarantee */}
            <div className="mt-8 flex items-center justify-center gap-2 text-gray-400 animate-fade-in-up animation-delay-400">
              <Shield className="w-5 h-5 text-[#a3e635]" />
              <span className="text-sm">{t('landing.socialProofBadge')}</span>
              <span className="text-gray-600">•</span>
              <span className="text-sm">{t('landing.noCommitment')}</span>
            </div>
            
            {/* Scroll Indicator */}
            <div className="mt-12 animate-bounce">
              <ChevronDown className="w-8 h-8 text-[#a3e635]/50 mx-auto" />
            </div>
          </div>
        </div>
      </section>

      {/* ================================================================
          4 PILLARS SECTION - UNLIMITED VALUE PROPOSITION
      ================================================================ */}
      <section className="relative z-10 py-16 sm:py-20 px-4 sm:px-6">
        <div className="container mx-auto max-w-5xl">
          {/* Section Header */}
          <div className="text-center mb-10 sm:mb-14">
            {/* Price Badge */}
            <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#a3e635]/10 border border-[#a3e635]/40 mb-6">
              <Sparkles className="w-4 h-4 text-[#a3e635]" />
              <span className="text-[#a3e635] font-bold text-sm">
                {t('landing.unlimited.badge')}
              </span>
            </div>
            
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-white mb-3 leading-tight">
              {t('landing.pillars.title')}
            </h2>
            <p className="text-gray-400 text-base sm:text-lg max-w-xl mx-auto">
              {t('landing.pillars.subtitle')}
            </p>
          </div>
          
          {/* 4 Pillars Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {/* SCAN - With Try Free Button */}
            <button
              onClick={() => setShowSpiritScan(true)}
              className="group p-5 sm:p-6 rounded-2xl bg-zinc-900/50 border border-zinc-800 
                          hover:border-green-500/50 hover:bg-zinc-900/80 transition-all duration-300
                          hover:shadow-[0_0_30px_rgba(34,197,94,0.15)] text-left cursor-pointer"
            >
              <div className="w-14 h-14 sm:w-16 sm:h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-green-500 to-emerald-500 
                            flex items-center justify-center shadow-lg shadow-green-500/20
                            group-hover:scale-110 transition-transform duration-300">
                <Camera className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
              </div>
              <h3 className="text-lg font-bold text-white text-center mb-2">{t('landing.pillars.scan.title')}</h3>
              <p className="text-gray-400 text-sm text-center mb-3">{t('landing.pillars.scan.desc')}</p>
              <div className="text-center">
                <span className="inline-flex items-center gap-1 text-xs font-bold text-green-400 bg-green-500/10 px-3 py-1 rounded-full">
                  <Camera className="w-3 h-3" />
                  {t('landing.pillars.scan.tryFree')}
                </span>
              </div>
            </button>
            
            {/* PLAN */}
            <div className="group p-5 sm:p-6 rounded-2xl bg-zinc-900/50 border border-zinc-800 
                          hover:border-amber-500/50 hover:bg-zinc-900/80 transition-all duration-300
                          hover:shadow-[0_0_30px_rgba(245,158,11,0.15)]">
              <div className="w-14 h-14 sm:w-16 sm:h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-500 
                            flex items-center justify-center shadow-lg shadow-amber-500/20
                            group-hover:scale-110 transition-transform duration-300">
                <UtensilsCrossed className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
              </div>
              <h3 className="text-lg font-bold text-white text-center mb-2">{t('landing.pillars.plan.title')}</h3>
              <p className="text-gray-400 text-sm text-center">{t('landing.pillars.plan.desc')}</p>
            </div>
            
            {/* TRAINING */}
            <div className="group p-5 sm:p-6 rounded-2xl bg-zinc-900/50 border border-zinc-800 
                          hover:border-blue-500/50 hover:bg-zinc-900/80 transition-all duration-300
                          hover:shadow-[0_0_30px_rgba(59,130,246,0.15)]">
              <div className="w-14 h-14 sm:w-16 sm:h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-500 
                            flex items-center justify-center shadow-lg shadow-blue-500/20
                            group-hover:scale-110 transition-transform duration-300">
                <Dumbbell className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
              </div>
              <h3 className="text-lg font-bold text-white text-center mb-2">{t('landing.pillars.training.title')}</h3>
              <p className="text-gray-400 text-sm text-center">{t('landing.pillars.training.desc')}</p>
            </div>
            
            {/* SPIRIT */}
            <div className="group p-5 sm:p-6 rounded-2xl bg-zinc-900/50 border border-zinc-800 
                          hover:border-purple-500/50 hover:bg-zinc-900/80 transition-all duration-300
                          hover:shadow-[0_0_30px_rgba(168,85,247,0.15)]">
              <div className="w-14 h-14 sm:w-16 sm:h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 
                            flex items-center justify-center shadow-lg shadow-purple-500/20
                            group-hover:scale-110 transition-transform duration-300">
                <Sparkles className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
              </div>
              <h3 className="text-lg font-bold text-white text-center mb-2">{t('landing.pillars.spirit.title')}</h3>
              <p className="text-gray-400 text-sm text-center">{t('landing.pillars.spirit.desc')}</p>
            </div>
          </div>
          
          {/* Price CTA */}
          <div className="mt-10 sm:mt-12 text-center">
            <div className="inline-flex flex-col items-center">
              <div className="flex items-baseline gap-2 mb-2">
                <span className="text-4xl sm:text-5xl font-black text-white">€12.99</span>
                <span className="text-gray-400 text-lg">/{t('subscription.month')}</span>
              </div>
              <p className="text-sm text-gray-500 mb-4">{t('landing.unlimited.pricePerDay')}</p>
              <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-400">
                <span className="flex items-center gap-1">
                  <Check className="w-4 h-4 text-[#a3e635]" />
                  {t('landing.unlimited.badge1')}
                </span>
                <span className="flex items-center gap-1">
                  <Check className="w-4 h-4 text-[#a3e635]" />
                  {t('landing.unlimited.badge2')}
                </span>
                <span className="flex items-center gap-1">
                  <Check className="w-4 h-4 text-[#a3e635]" />
                  {t('landing.unlimited.badge3')}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================================================================
          FEATURES SECTION
      ================================================================ */}
      <section id="features" className="relative z-10 py-20 sm:py-28 px-4 sm:px-6">
        <div className="container mx-auto max-w-6xl">
          {/* Section Title */}
          <div className="text-center mb-12 sm:mb-20">
            <h2 className="text-2xl sm:text-4xl lg:text-5xl font-black text-white mb-4 sm:mb-6 leading-tight">
              {t('landing.whyKetoLab')}<span className="text-[#a3e635]">Keto Lab Method</span>{t('landing.whyKetoLabSuffix')}
            </h2>
            <p className="text-base sm:text-lg text-gray-400 max-w-2xl mx-auto leading-relaxed px-2">
              {t('landing.features.subtitle')}
            </p>
          </div>
          
          {/* Features Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            <NeonFeatureCard 
              icon={Target}
              title={t('landing.features.personalizedPlan')}
              description={t('landing.features.personalizedPlanDesc')}
              delay={0}
            />
            <NeonFeatureCard 
              icon={Flame}
              title={t('landing.features.intermittentFasting')}
              description={t('landing.features.intermittentFastingDesc')}
              delay={100}
            />
            <NeonFeatureCard 
              icon={Brain}
              title={t('landing.features.aiTracking')}
              description={t('landing.features.aiTrackingDesc')}
              delay={200}
            />
            <NeonFeatureCard 
              icon={Users}
              title={t('landing.features.community')}
              description={t('landing.features.communityDesc')}
              delay={300}
            />
          </div>
        </div>
      </section>

      {/* ================================================================
          ECOSYSTEM MAP SECTION - Interactive visualization
      ================================================================ */}
      <section className="relative z-10 py-28 px-6">
        <div className="container mx-auto">
          <EcosystemMap onModuleClick={(moduleId) => {
            // Scroll to auth section when clicking modules
            document.getElementById('auth')?.scrollIntoView({ behavior: 'smooth' });
          }} />
        </div>
      </section>

      {/* ================================================================
          LIVE STATS COUNTER SECTION - Real-time animated stats
      ================================================================ */}
      <section className="relative z-10 py-28 px-6">
        <LiveStatsCounter />
      </section>

      {/* ================================================================
          DAY WITH KETOLAB - Timeline Section
      ================================================================ */}
      <section className="relative z-10 py-24 px-6 bg-gradient-to-b from-transparent via-[#a3e635]/5 to-transparent">
        <div className="container mx-auto">
          <DayWithKetoLab />
        </div>
      </section>

      {/* ================================================================
          GAMIFICATION PREVIEW - Badges & Tiers
      ================================================================ */}
      <section className="relative z-10 py-24 px-6">
        <div className="container mx-auto">
          <GamificationPreview />
        </div>
      </section>

      {/* ================================================================
          STATS SECTION - DESAFIO 8K HIGHLIGHT
      ================================================================ */}
      <section className="relative z-10 py-16 px-6">
        <div className="container mx-auto max-w-5xl">
          <div className="relative p-8 md:p-12 rounded-3xl bg-black
                          border-2 border-[#a3e635]/30 overflow-hidden"
               style={{ backgroundColor: '#0a0a0a' }}>
            {/* Background accent */}
            <div className="absolute -top-20 -right-20 w-60 h-60 bg-[#a3e635]/10 rounded-full blur-3xl" />
            <div className="absolute -bottom-20 -left-20 w-60 h-60 bg-[#39FF14]/10 rounded-full blur-3xl" />
            
            <div className="relative grid grid-cols-2 md:grid-cols-4 gap-6">
              {/* DESAFIO 8K - EXCLUSIVE PROGRAM HIGHLIGHT */}
              <div className="relative group p-4 sm:p-6 rounded-2xl bg-gradient-to-br from-[#a3e635]/20 to-[#39FF14]/10
                            border-2 border-[#a3e635] text-center min-h-[120px] flex flex-col justify-center
                            shadow-[0_0_30px_rgba(163,230,53,0.4)]
                            hover:shadow-[0_0_50px_rgba(163,230,53,0.6)] transition-all duration-300">
                {/* Exclusive badge */}
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full
                              bg-[#a3e635] text-black text-[10px] font-bold uppercase tracking-wider
                              shadow-[0_0_15px_rgba(163,230,53,0.8)]">
                  Exclusivo
                </div>
                <Zap className="w-6 h-6 text-[#a3e635] mx-auto mb-2 drop-shadow-[0_0_10px_rgba(163,230,53,1)]" />
                <div className="text-2xl sm:text-3xl md:text-4xl font-black text-[#a3e635] leading-tight
                              drop-shadow-[0_0_15px_rgba(163,230,53,0.8)]">
                  8K
                </div>
                <div className="text-xs sm:text-sm text-white mt-1 uppercase tracking-wider font-bold">
                  {t('landing.challenge8k')}
                </div>
              </div>
              
              <AnimatedStatCard 
                value="5K+" 
                label={t('landing.activeMembers')}
                icon={Users}
              />
              <AnimatedStatCard 
                value="95%" 
                label={t('landing.successRate')}
                icon={Star}
              />
              <AnimatedStatCard 
                value="24/7" 
                label={t('landing.aiSupport')}
                icon={Shield}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ================================================================
          AUTH SECTION
      ================================================================ */}
      <section id="auth" className="relative z-10 py-20 px-6">
        <div className="container mx-auto max-w-md">
          <div className="relative p-8 rounded-3xl bg-black/60 backdrop-blur-2xl border border-[#a3e635]/30
                          shadow-2xl shadow-[#a3e635]/10 overflow-hidden">
            {/* Glow effects */}
            <div className="absolute -top-10 -right-10 w-40 h-40 bg-[#a3e635]/20 rounded-full blur-3xl" />
            <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-[#39FF14]/10 rounded-full blur-3xl" />
            
            {/* Logo */}
            <div className="relative flex justify-center mb-8">
              <div className="w-24 h-24 rounded-2xl overflow-hidden border-2 border-[#a3e635]/50
                              shadow-lg shadow-[#a3e635]/30">
                <img 
                  src="/keto-lab-logo-new.jpg" 
                  alt="Keto Lab" 
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>

            {/* Title */}
            <h2 className="text-3xl font-bold text-center mb-8">
              <span className="bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                {isLogin ? t('landing.welcomeBack') : t('landing.joinUs')}
              </span>
            </h2>

            {/* Error */}
            {error && (
              <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/30">
                <p className="text-red-400 text-center text-sm">{error}</p>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="relative space-y-5">
              {!isLogin && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    {t('auth.name')}
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl bg-black/50 border border-[#a3e635]/30 text-white
                               placeholder:text-gray-500 focus:border-[#a3e635] focus:ring-2 focus:ring-[#a3e635]/20
                               transition-all duration-300 outline-none"
                    placeholder={t('landing.yourName')}
                    required={!isLogin}
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl bg-black/50 border border-[#a3e635]/30 text-white
                             placeholder:text-gray-500 focus:border-[#a3e635] focus:ring-2 focus:ring-[#a3e635]/20
                             transition-all duration-300 outline-none"
                  placeholder={t('landing.yourEmail')}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {t('auth.password')}
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl bg-black/50 border border-[#a3e635]/30 text-white
                             placeholder:text-gray-500 focus:border-[#a3e635] focus:ring-2 focus:ring-[#a3e635]/20
                             transition-all duration-300 outline-none"
                  placeholder="••••••••"
                  required
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 rounded-xl bg-gradient-to-r from-[#a3e635] to-[#39FF14] text-black font-bold
                           text-lg hover:shadow-lg hover:shadow-[#a3e635]/40 transition-all duration-300
                           disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.98]
                           relative overflow-hidden group"
              >
                <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent
                                -translate-x-full group-hover:translate-x-full transition-transform duration-500" />
                <span className="relative flex items-center justify-center gap-2">
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      {isLogin ? t('auth.login') : t('landing.startFreeTrial')}
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </span>
              </button>

              {/* Forgot Password Link - Only show on login */}
              {isLogin && (
                <div className="text-center">
                  <a
                    href="/forgot-password"
                    className="text-sm text-gray-400 hover:text-[#a3e635] transition-colors"
                  >
                    Esqueci a password
                  </a>
                </div>
              )}

              {/* Toggle */}
              <p className="text-center text-gray-400 text-sm">
                {isLogin 
                  ? t('auth.noAccount')
                  : t('auth.hasAccount')
                }
                <button
                  type="button"
                  onClick={() => { setIsLogin(!isLogin); setError(''); }}
                  className="ml-2 text-[#a3e635] hover:text-[#39FF14] font-medium transition-colors"
                >
                  {isLogin ? t('auth.register') : t('auth.login')}
                </button>
              </p>
            </form>
          </div>
        </div>
      </section>

      {/* ================================================================
          TESTIMONIALS SECTION - Social Proof
      ================================================================ */}
      <TestimonialsSection />

      {/* ================================================================
          FOOTER
      ================================================================ */}
      <footer className="relative z-10 py-10 px-6 border-t border-[#a3e635]/10">
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col items-center gap-4">
            {/* Logo and Brand */}
            <div className="flex items-center gap-3">
              <img src="/logo-ketolab-method.png" alt="Keto Lab Method" className="h-12 w-auto" loading="lazy" />
            </div>
            
            {/* Footer Links */}
            <div className="flex flex-wrap justify-center gap-6 text-sm">
              <a href="/faq" className="text-gray-400 hover:text-[#a3e635] transition-colors">FAQ</a>
              <a href="/legal" className="text-gray-400 hover:text-[#a3e635] transition-colors">{t('footer.legal') || 'Termos & Condições'}</a>
              <a href="mailto:support@ketolab.pt" className="text-gray-400 hover:text-[#a3e635] transition-colors">{t('footer.contact') || 'Contacto'}</a>
            </div>
            
            {/* Copyright */}
            <div className="text-center">
              <p className="text-gray-400 text-sm">
                © {new Date().getFullYear()} Keto Lab Method®. {t('footer.allRightsReserved')}
              </p>
              <p className="text-gray-600 text-xs mt-1">
                {t('footer.trademark')}
              </p>
            </div>
          </div>
        </div>
      </footer>

      {/* Spirit Scan Public Modal */}
      <SpiritScanPublic 
        isOpen={showSpiritScan}
        onClose={() => setShowSpiritScan(false)}
        onSignUp={() => {
          setShowSpiritScan(false);
          document.getElementById('auth')?.scrollIntoView({ behavior: 'smooth' });
        }}
      />
    </div>
  );
};

export default LandingPagePremium;
